import './instrument'; // MUST be the first import — Sentry must initialize before any other code

import { Auth0Provider } from '@auth0/auth0-react';
import { Auth0OrganizationData, Auth0OrganizationID, Auth0OrganizationName, Auth0UserMembership } from '@gr/shared/models';
import { addParam, getRedirectUrl } from '@gr/shared/utils';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import AppMain from './AppMain';
import { AUTH0_MAIN_CONFIG, Auth0ConfigContextProvider } from './contexts/Auth0ConfigContext';
import { IMembershipContext, MembershipContextProvider } from './contexts/MembershipContext';
import { Auth0FrontendConfig, getSubDomain, getUrlParam } from './providers/auth0-data.provider';
import { getProductAuth0Domain, getProductBaseDomainUrl } from './providers/auth0-product.provider';
import { Auth0LocalStorage } from './providers/auth0-storage.provider';
import { AUTH0_AUDIENCE, createAuth0Scope } from './providers/auth0.service';
import './styles.scss';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
    }
  }
});

const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Something went wrong</p>} showDialog>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Auth0App />
        </BrowserRouter>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);


// ---- Auth0 Stuff ----
function Auth0App(): JSX.Element {
  const BASE_URI = getRedirectUrl(window.location.origin);

  const [auth0Config, setAuth0Config] = useState<Auth0FrontendConfig | undefined>(undefined);
  const [loadedConfig, setLoadedConfig] = useState<Auth0FrontendConfig | undefined>(undefined);

  const getOrganizationId = (): Auth0OrganizationID | undefined => {
    return getUrlParam('organization')
      ?? auth0Config?.organizationId
      ?? undefined;
  };

  const getOrganizationName = (): Auth0OrganizationName | undefined => {
    const subDomain = getSubDomain();
    const targetOrgFromEnv = isLocal ? import.meta.env.VITE_TARGET_ORG : undefined;

    return getUrlParam('org')
      ?? auth0Config?.organization
      ?? targetOrgFromEnv
      ?? subDomain
      ?? undefined;
  };

  interface GetRedirectUrlRequest {
    orgId: string | undefined;
    orgName: string | undefined;
  }

  const getMainRedirectUrl = ({ orgId, orgName }: GetRedirectUrlRequest) => {
    return addParam(getOrgRedirectUrl({ orgId, orgName }), `main=true`);
  };

  const getOrgRedirectUrl = ({ orgId, orgName }: GetRedirectUrlRequest) => {
    const params: string[] = [];

    if (orgName) {
      params.push(`org=${orgName}`);
    }

    if (orgId) {
      params.push(`organization=${orgId}`);
    }

    return `${BASE_URI}?${params.join('&')}`;
  };

  const isLocal = window.location.origin.includes('localhost');

  // Auth0 Controlled Params
  const invitation = getUrlParam('invitation');
  const orgId = getOrganizationId();

  // Custom Params
  const orgName = getOrganizationName();

  const [membership, setMembership] = useState<Auth0UserMembership>();
  const [organizations, setOrganizations] = useState<Auth0OrganizationData[]>();
  const [isMembershipLoaded, setMembershipLoaded] = useState(false);
  const [auth0LocalStorage, setAuth0LocalStorage] = useState<Auth0LocalStorage | undefined>();
  const [organizationsReady, setOrganizationsReady] = useState(false);

  const membershipContext: IMembershipContext = {
    membership,
    setMembership,

    organizations,
    setOrganizations,

    auth0LocalStorage,
    setAuth0LocalStorage,

    isMembershipLoaded,
    setMembershipLoaded,

    organizationsReady,
    setOrganizationsReady,
  };

  const setConfig = (config: Auth0FrontendConfig) => {
    setAuth0Config(config);
    setLoadedConfig(config);
  };

  useEffect(() => {
    if (!!auth0Config && !!loadedConfig && auth0Config.organization !== loadedConfig.organization) {
      setLoadedConfig(undefined);
    }
  }, [auth0Config, loadedConfig]);

  return (
    <Auth0ConfigContextProvider value={{ auth0Config, setAuth0Config }}>
      <MembershipContextProvider value={membershipContext}>
        {!loadedConfig && (
          <Auth0Provider
            domain={getProductAuth0Domain()}
            skipRedirectCallback={!window.location.href.includes(`main=`)}
            clientId={AUTH0_MAIN_CONFIG.clientId}
            cacheLocation={!!invitation ? 'memory' : 'localstorage'}
            cookieDomain={isLocal ? 'localhost' : `.${getProductBaseDomainUrl()}`}
            useRefreshTokens={true}
            useRefreshTokensFallback={true}
            authorizationParams={{
              redirect_uri: getMainRedirectUrl({ orgId, orgName }),
              invitation: invitation,
              organization: AUTH0_MAIN_CONFIG.organizationId,
              scope: createAuth0Scope({ orgName: AUTH0_MAIN_CONFIG.organization }),
              audience: AUTH0_AUDIENCE,
            }}
          >
            <AppMain
              targetOrgId={orgId}
              targetOrgName={orgName}
              setAuth0Config={setConfig}
            />
          </Auth0Provider>
        )}

        {!!loadedConfig && (
          <Auth0Provider
            key={`${loadedConfig.clientId}_${loadedConfig.organizationId}`}
            domain={getProductAuth0Domain()}
            skipRedirectCallback={window.location.href.includes('main=')}
            clientId={loadedConfig.clientId}
            cacheLocation={'localstorage'}
            cookieDomain={isLocal ? 'localhost' : `.${getProductBaseDomainUrl()}`}
            useRefreshTokens={true}
            useRefreshTokensFallback={true}
            authorizationParams={{
              redirect_uri: getOrgRedirectUrl({ orgId: loadedConfig.organizationId, orgName: loadedConfig.organization }),
              organization: loadedConfig.organizationId,
              scope: createAuth0Scope({ orgName: loadedConfig.organization }),
              audience: AUTH0_AUDIENCE,
            }}
          >
            <App
              auth0Config={loadedConfig}
            />
          </Auth0Provider>
        )}
      </MembershipContextProvider>
    </Auth0ConfigContextProvider>
  );
}